import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { Button, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { alertActions } from "_store";
import { fileExtension, fileSizeReadable, fileTypeAcceptable } from "_utils";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { materialsymbolsupload, materialsymbolsdownload } from "images";
import { wg_report_logo } from "images";
import { sanitizeNumber } from "./MatrixComplianceUtils";

const header = "Matrix Compliance";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const MatrixComplianceUpload = ({
  isAdmin,
  rows = [],
  dateRange = [],
  dateSendoutMap = {},
  onApplyAdminRows,
  onApplyMarketerRows,
  setIsDataChanged,
  supportedFormats = [".xlsx", ".xls"],
  canEdit,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [fileNames, setFileNames] = useState([]);

  const requiredHeaders = isAdmin ? ["Marketer", ...dateRange] : ["Date", "Sendout", "Value"];

  const handleError = (message) => {
    dispatch(alertActions.error({ message, header }));
  };

  // ─── Download sample template (admin/non-admin shape) ────────────────────
  const downloadSample = async () => {
    dispatch(alertActions.clear());

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Matrix Compliance");

    try {
      const response = await fetch(wg_report_logo);
      const imageBlob = await response.blob();
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(imageBlob);
      });
      const imageId = workbook.addImage({ base64, extension: "png" });
      worksheet.addImage(imageId, {
        tl: { col: requiredHeaders.length, row: 0 },
        ext: { width: 170, height: 60 },
      });
    } catch (_) {
      // non-fatal
    }

    worksheet.getRow(1).height = 65;
    worksheet.mergeCells(`A1:${String.fromCharCode(65 + requiredHeaders.length - 1)}1`);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "Matrix Compliance";
    titleCell.font = { bold: true, size: 16, color: { argb: "FF0059B3" } };
    titleCell.alignment = { horizontal: "left", vertical: "middle" };

    const noteRow = worksheet.addRow(["Date Format: mm/dd/yyyy"]);
    worksheet.mergeCells(`A${noteRow.number}:${String.fromCharCode(65 + requiredHeaders.length - 1)}${noteRow.number}`);
    noteRow.getCell(1).font = { italic: true, size: 10, color: { argb: "FF666666" } };

    worksheet.addRow([]);

    let dataRows;
    if (isAdmin) {
      dataRows =
        rows.length > 0
          ? rows.map((r) => {
              const entry = { Marketer: String(r.CompanyName || "") };
              dateRange.forEach((date) => (entry[date] = r[date] ?? "0"));
              return entry;
            })
          : [(() => {
              const entry = { Marketer: "test" };
              dateRange.forEach((date) => (entry[date] = "0"));
              return entry;
            })()];
    } else {
      dataRows =
        rows.length > 0
          ? rows.map((r) => ({ Date: r.date, Sendout: r.sendout ?? dateSendoutMap?.[r.date] ?? "0", Value: r.value ?? "0" }))
          : dateRange.map((date) => ({ Date: date, Sendout: dateSendoutMap?.[date] ?? "0", Value: "0" }));
    }

    const headerRow = worksheet.addRow(requiredHeaders);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0059B3" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    if (isAdmin) {
      const sendoutRow = worksheet.addRow(
        requiredHeaders.map((col) => (col === "Marketer" ? "Sendout" : String(dateSendoutMap?.[col] ?? "0")))
      );
      sendoutRow.eachCell((cell) => {
        cell.font = { bold: true, italic: true, color: { argb: "FF1A3A5C" } };
        cell.alignment = { horizontal: "center" };
      });
    }

    dataRows.forEach((rowData) => {
      const row = worksheet.addRow(requiredHeaders.map((col) => rowData[col] ?? ""));
      row.eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    requiredHeaders.forEach((col, i) => {
      const maxLen = Math.max(col.length, ...dataRows.map((r) => String(r[col] ?? "").length));
      worksheet.getColumn(i + 1).width = Math.max(maxLen + 2, 14);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "MatrixCompliance.xlsx"
    );
  };

  // ─── Apply uploaded sheet data ────────────────────────────────────────────
  const applyAdminSheet = (jsonData) => {
    const nonEmptyRows = jsonData.filter((row) => Object.values(row).some((v) => String(v).trim() !== ""));
    if (nonEmptyRows.length === 0) {
      handleError("The uploaded sheet contains no data rows.");
      return false;
    }

    const uploadMap = {};
    const invalidMarketers = [];

    nonEmptyRows.forEach((row) => {
      const marketerName = String(row["Marketer"] || "").trim();
      if (!marketerName || marketerName.toLowerCase() === "sendout") return;

      const matchedRow = rows.find(
        (r) => String(r.CompanyName).trim().toLowerCase() === marketerName.toLowerCase()
      );
      if (!matchedRow) {
        invalidMarketers.push(marketerName);
        return;
      }

      uploadMap[marketerName.toLowerCase()] = {};
      dateRange.forEach((date) => {
        if (!(date in row)) return;
        uploadMap[marketerName.toLowerCase()][date] = sanitizeNumber(row[date]) || "0";
      });
    });

    if (Object.keys(uploadMap).length === 0) {
      handleError("None of the Marketer names in the uploaded file matched the current table.");
      return false;
    }

    onApplyAdminRows(uploadMap);

    if (invalidMarketers.length > 0) {
      dispatch(
        alertActions.error({
          message: `${invalidMarketers.length} row(s) were skipped because their Marketer names do not exist in the current view: ${invalidMarketers.join(", ")}`,
          header,
        })
      );
    }

    return true;
  };

  const applyMarketerSheet = (jsonData) => {
    const nonEmptyRows = jsonData.filter((row) => Object.values(row).some((v) => String(v).trim() !== ""));
    if (nonEmptyRows.length === 0) {
      handleError("The uploaded sheet contains no data rows.");
      return false;
    }

    const uploadMap = {};
    const invalidDates = [];

    nonEmptyRows.forEach((row) => {
      const date = String(row["Date"] || "").trim();
      if (!date) return;

      const matchedRow = rows.find((r) => r.date === date);
      if (!matchedRow) {
        invalidDates.push(date);
        return;
      }

      uploadMap[date] = sanitizeNumber(row["Value"]) || "0";
    });

    if (Object.keys(uploadMap).length === 0) {
      handleError("None of the Dates in the uploaded file matched the current table.");
      return false;
    }

    onApplyMarketerRows(uploadMap);

    if (invalidDates.length > 0) {
      dispatch(
        alertActions.error({
          message: `${invalidDates.length} row(s) were skipped because their Dates are outside the current filtered range: ${invalidDates.join(", ")}`,
          header,
        })
      );
    }

    return true;
  };

  // ─── Main file-change handler ─────────────────────────────────────────────
  const handleChange = async (event) => {
    dispatch(alertActions.clear());
    event.preventDefault();

    const filesAdded = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    if (!filesAdded || filesAdded.length === 0) return;

    const file = filesAdded[0];
    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    file.extension = fileExtension(file);
    file.sizeReadable = fileSizeReadable(file.size);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      handleError(`File size cannot exceed ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    if (!fileTypeAcceptable(supportedFormats, file)) {
      handleError("Invalid file format. Only Excel files are supported.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const requiredKey = isAdmin ? "marketer" : "date";
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
        const headerRowIndex = rawRows.findIndex((row) =>
          row.some((cell) => String(cell).trim().toLowerCase() === requiredKey)
        );

        if (headerRowIndex === -1) {
          handleError(
            `The uploaded sheet does not match the required format. Expected columns: ${requiredHeaders.join(", ")}.`
          );
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(sheet, { range: headerRowIndex, defval: "", raw: false });

        const applied = isAdmin ? applyAdminSheet(jsonData) : applyMarketerSheet(jsonData);

        if (applied) {
          setIsDataChanged(true);
          setFileNames([file.name]);
        }
      } catch (err) {
        handleError("An unexpected error occurred while reading the file.");
        console.error(err);
      }
    };

    reader.readAsArrayBuffer(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Typography component="div" className="UploadContainer">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            component="label"
            variant="contained"
            disabled={!canEdit}
            className="Uploadfiles"
            startIcon={<img src={materialsymbolsupload} alt="Upload" />}
          >
            <span>Upload your files here</span>
            <VisuallyHiddenInput type="file" onChange={handleChange} ref={fileInputRef} />
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography component="div" className="SupportedFormats">
            <Typography component="h3">Supported Formats</Typography>
            <Typography component="div" className="fileformatlist">
              {supportedFormats.map((fmt) => (
                <span key={fmt}>{fmt} </span>
              ))}
            </Typography>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography component="div" className="SupportedFormats">
            <Typography component="h3">Download Template</Typography>
            <div className="mar-top-16">
              <Typography component="div">
                Sample File Format
                <IconButton onClick={downloadSample}>
                  <img src={materialsymbolsdownload} alt="material-symbols_download" />
                </IconButton>
              </Typography>
            </div>
          </Typography>
        </Grid>

        {fileNames.length > 0 && (
          <Grid item xs={12}>
            <Typography component="div" className="SupportedFormats">
              <Typography component="h3">Uploaded File</Typography>
              {fileNames.map((name) => (
                <Typography key={name} component="div" style={{ fontSize: 13 }}>
                  {name}
                </Typography>
              ))}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Typography>
  );
};

export default MatrixComplianceUpload;
